import React, { useState, useRef, useEffect } from 'react'
import { scrollAnim, Button, ButtonsGroup } from '@brainstud/react-helpers'
import useStepsState from './hooks/useStepsState'
import Hotspot from '../shared/hotspot/Hotspot'
import styles from './TourPlugin.module.css'

function TourPlugin(props) {
  const {
    children,
    triggerComponent,
    tooltipComponent,
    triggerStyle,
    tooltipStyle: globalTooltipStyle,
    maskStyle: globalMaskStyle,
    translations,
    maskPadding,
    tooltipSeparation,
  } = props

  const [visible, setVisible] = useState(false)
  const [currentStep, setCurrentStep] = useState(null)
  const steps = useStepsState(children)
  const [maskStyle, setMaskStyle] = useState({})
  const [tooltipStyle, setTooltipStyle] = useState({})
  const tooltipEl = useRef(null)

  // Handle mask and tooltip positions
  useEffect(() => {
    if(!currentStep) return

    const targetRect = currentStep.el().getBoundingClientRect()
    const tooltipRect = tooltipEl.current.getBoundingClientRect()

    const maskWidth = targetRect.width + maskPadding * 2
    const maskHeight = targetRect.height + maskPadding * 2
    const maskTop = targetRect.top + window.pageYOffset - maskPadding
    const maskLeft = targetRect.left - maskPadding

    const tooltipWidth = tooltipRect.width
    const tooltipHeight = tooltipRect.height
    
    const tooltipPositions = {
      top: {
        top: { topOffset: 0, leftOffset: 0 },
        bottom: { topOffset: 0, leftOffset: 0 },
        left: {
          topOffset: maskTop - tooltipHeight - tooltipSeparation,
          leftOffset: maskLeft,
        },
        center: {
          topOffset: maskTop - tooltipHeight - tooltipSeparation,
          leftOffset: maskLeft + (maskWidth - tooltipWidth) / 2,
        },
        right: {
          topOffset: maskTop - tooltipHeight - tooltipSeparation,
          leftOffset: maskLeft + maskWidth - tooltipWidth,
        },
      },
      right: {
        left: { topOffset: 0, leftOffset: 0 },
        right: { topOffset: 0, leftOffset: 0 },
        top: {
          topOffset: maskTop,
          leftOffset: maskLeft + maskWidth + tooltipSeparation,
        },
        center: {
          topOffset: maskTop + (maskHeight - tooltipHeight) / 2,
          leftOffset: maskLeft + maskWidth + tooltipSeparation,
        },
        bottom: {
          topOffset: maskTop + maskHeight - tooltipHeight,
          leftOffset: maskLeft + maskWidth + tooltipSeparation,
        },
      },
      bottom: {
        top: { topOffset: 0, leftOffset: 0 },
        bottom: { topOffset: 0, leftOffset: 0 },
        left: {
          topOffset: maskTop + maskHeight + tooltipSeparation,
          leftOffset: maskLeft,
        },
        center: {
          topOffset: maskTop + maskHeight + tooltipSeparation,
          leftOffset: maskLeft + (maskWidth - tooltipWidth) / 2,
        },
        right: {
          topOffset: maskTop + maskHeight + tooltipSeparation,
          leftOffset: maskLeft + maskWidth - tooltipWidth,
        },
      },
      left: {
        left: { topOffset: 0, leftOffset: 0 },
        right: { topOffset: 0, leftOffset: 0 },
        top: {
          topOffset: maskTop,
          leftOffset: maskLeft - tooltipWidth - tooltipSeparation,
        },
        center: {
          topOffset: maskTop + (maskHeight - tooltipHeight) / 2,
          leftOffset: maskLeft - tooltipWidth - tooltipSeparation,
        },
        bottom: {
          topOffset: maskTop + maskHeight - tooltipHeight,
          leftOffset: maskLeft - tooltipWidth - tooltipSeparation,
        },
      },
    }

    let tooltipTop = tooltipPositions[currentStep.tooltip.position][currentStep.tooltip.alignment].topOffset
    let tooltipLeft = tooltipPositions[currentStep.tooltip.position][currentStep.tooltip.alignment].leftOffset

    const nextMaskStyle = {
      width: `${maskWidth}px`,
      height: `${maskHeight}px`,
      top: `${maskTop}px`,
      left: `${maskLeft}px`,
      ...currentStep.maskStyle,
    }

    const nextTooltipStyle = {
      top: `${tooltipTop}px`,
      left: `${tooltipLeft}px`,
      ...currentStep.tooltipStyle,
    }

    // step.el().classList.add('tour-plugin-mask')
    setMaskStyle(nextMaskStyle)
    setTooltipStyle(nextTooltipStyle)
  }, [currentStep])

  // Handle resize and ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if(visible) toggleTour()
      }
    }

    const handleResize = (e) => {
      if(visible) {
        reset()
      }
    }

    if(visible) {
      window.addEventListener('keydown', handleKeyDown, false)
      window.addEventListener('resize', handleResize, false)
      scrollAnim()
    } else {
      window.removeEventListener('keydown', handleKeyDown, false)
      window.removeEventListener('resize', handleResize, false)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown, false)
      window.removeEventListener('resize', handleResize, false)
    }
  }, [visible])

  const toggleTour = () => {
    reset()
    setVisible(!visible)
  }

  const handleNext = () => {
    const nextStep = steps[steps.indexOf(currentStep) + 1] || steps[0]
    
    setCurrentStep(nextStep)
  }

  const reset = () => {
    // document.querySelectorAll('.tour-plugin-mask').forEach(elm => {
    //   elm.classList.remove('tour-plugin-mask')
    // })
    setMaskStyle({})
    setTooltipStyle({})
    setCurrentStep(null)
  }

  const tooltipClassNames = currentStep &&
    `tooltip-has-position-${currentStep.tooltip.position} tooltip-has-alignment-${currentStep.tooltip.alignment}`

  return (
    <div className={styles.base}>
      <div className={styles.trigger} style={triggerStyle}>
        {triggerComponent ? (
          React.cloneElement(triggerComponent, {
            toggleTour,
            visible,
          })
        ) : (
          <Button
            onClick={toggleTour}
            label={visible ? translations.closeButton : translations.openButton}
          />
        )}
      </div>
      
      {visible &&
        <div className={styles.layer}>
          {steps.map((step, idx) => (
            <Hotspot
              key={idx}
              {...step}
              visible={(currentStep && currentStep.el) !== step.el}
              onClick={() => setCurrentStep(step)}
            />
          ))}
        </div>
      }

      {/* {(visible && !currentStep) &&
        <div className={styles.overlay} />
      } */}

      {visible &&
        <div
          className={styles.mask}
          style={{ ...maskStyle, ...globalMaskStyle }}
        />
      }

      {currentStep && 
        <div
          ref={tooltipEl}
          className={styles.tooltip}
          style={{ ...tooltipStyle, ...globalTooltipStyle }} 
        >
          {tooltipComponent ? (
            React.cloneElement(tooltipComponent, {
              handleNext,
              handleExit: toggleTour,
              classNames: tooltipClassNames,
            }, currentStep.children)
          ) : (
            <div className={`${styles.tooltipContent} ${tooltipClassNames}`}>
              {currentStep.children}

              <ButtonsGroup full>
                <Button
                  onClick={toggleTour}
                  label={translations.exitButton}
                />
                <Button
                  onClick={handleNext}
                  label={translations.nextButton}
                />
              </ButtonsGroup>
            </div>
          )}
        </div>
      }
    </div>
  )
}

TourPlugin.defaultProps = {
  maskPadding: 10,
  tooltipSeparation: 20,
  triggerStyle: {},
  tooltipStyle: {},
  maskStyle: {},
  translations: {
    openButton: 'Helpen',
    closeButton: 'Uitgang',
    nextButton: 'Volgende',
    exitButton: 'Ik snap het',
  }
}

export default TourPlugin